 /**
 * Store Configuration Mock
 * Multi-tenant configuration object
 * Each store can customize these values
 */

import type {
  StoreConfig,
  Category,
  Product,
  BlogPost,
  User,
  TopBuyer,
  StoreGoal,
  DiscordWidget,
  WikiCategory,
  SupportTicket,
  Purchase,
  Order,
  Transaction,
  TicketCategory,
} from '@/types';

export const storeConfig: StoreConfig = {
  // Store Identity
  id: "hyterra",
  name: "Hyterra",
  slug: "hyterra",
  domain: "play.hyterra.net",
  
  // Branding
  logo: "/hystore.png",
  logoText: "Hyterra",
  favicon: "/favicon.ico",
  
  // Server Info
  serverIp: "play.hyterra.net",
  serverStatus: "online", // online, offline, maintenance
  playersOnline: 127,
  playersRecord: 1000,
  registeredPlayers: 50000,
  uptime: "24/7",
  
  // Theme (CSS Variables will be applied dynamically)
  theme: {
    primary: "217 91% 60%", // HSL format
    secondary: "215 28% 17%",
    accent: "160 84% 39%",
    background: "222 47% 11%",
    backgroundSecondary: "217 33% 17%",
    foreground: "210 40% 98%",
    card: "217 33% 17%",
    border: "215 28% 25%",
    radius: "0.75rem",
  },
  
  // Background Configuration
  background: {
    type: "image", // image, gradient, color
    image: "https://media.indiedb.com/images/games/1/70/69225/11.jpg",
    gradient: null,
    overlay: true,
    overlayOpacity: 0.7,
  },
  
  // Content
  heroTitle: "Junte-se à melhor experiência de jogos",
  heroSubtitle: "Construa seu império, lute com outros e torne-se o governante do mundo!",
  heroButtonText: "Jogar Agora",
  heroButtonAction: "copy_ip",
  
  // Social Links
  social: {
    discord: "https://discord.gg/hyterra",
    youtube: "https://youtube.com/@hyterra",
    instagram: "https://instagram.com/hyterra",
    twitter: "https://twitter.com/hyterra",
    tiktok: "https://tiktok.com/@hyterra",
    facebook: "https://facebook.com/hyterra",
  },
  
  // Navigation
  navigation: [
    { id: "home", label: "Início", href: "/", icon: "Home" },
    { id: "store", label: "Loja", href: "/loja", icon: "ShoppingCart" },
    { id: "forum", label: "Fórum", href: "/forum", icon: "MessageCircle" },
    { id: "help", label: "Wiki", href: "/wiki", icon: "HelpCircle" },
    { id: "support", label: "Suporte", href: "/suporte", icon: "Headphones" },
  ],
  
  // Footer
  footer: {
    about: "LeaderOS é um software tudo-em-um para servidores de jogos. Você pode gerenciar o site do seu servidor, monetizar, gerenciar tickets de suporte, criar fóruns e proporcionar experiências únicas aos seus jogadores com mais de 30 módulos.",
    quickLinks: [
      { label: "Início", href: "/" },
      { label: "Loja", href: "/loja" },
      { label: "Fórum", href: "/forum" },
      { label: "Wiki", href: "/wiki" },
      { label: "Suporte", href: "/suporte" },
    ],
    legalLinks: [
      { label: "Regras", href: "/regras" },
      { label: "Termos de Serviço", href: "/termos" },
      { label: "Política de Privacidade", href: "/privacidade" },
      { label: "Coleta de Dados", href: "/coleta-de-dados" },
    ],
  },
  
  // Currency
  currency: {
    code: "BRL",
    symbol: "R$",
    locale: "pt-BR",
  },
  
  // Language
  language: "pt-BR",
  
  // Features Toggle
  features: {
    store: true,
    forum: true,
    wiki: true,
    tickets: true,
    blog: true,
    credits: true,
  },
};

// Categories Mock
export const categories: Category[] = [
  {
    id: "vip",
    name: "VIP",
    slug: "vip",
    description: "Pacotes VIP exclusivos com benefícios especiais",
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&q=80",
    productCount: 5,
  },
  {
    id: "survival",
    name: "Survival",
    slug: "survival",
    description: "Itens e benefícios para o modo Survival",
    image: "https://images.unsplash.com/photo-1550100136-e092101726f4?w=600&q=80",
    productCount: 12,
  },
  {
    id: "skyblock",
    name: "Skyblock",
    slug: "skyblock",
    description: "Recursos exclusivos para Skyblock",
    image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=600&q=80",
    productCount: 8,
  },
  {
    id: "ranks",
    name: "Ranks",
    slug: "ranks",
    description: "Ranks permanentes com permissões especiais",
    image: "https://images.unsplash.com/photo-1534447677768-be436bb09401?w=600&q=80",
    productCount: 4,
  },
];

// Products Mock
export const products: Product[] = [
  {
    id: "vip-basic",
    categoryId: "vip",
    categorySlug: "vip",
    name: "VIP Básico",
    slug: "vip-basico",
    shortDescription: "Pacote VIP com benefícios essenciais para começar sua jornada.",
    description: "Pacote VIP com benefícios essenciais para começar sua jornada.",
    price: 19.90,
    originalPrice: 29.90,
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&q=80",
    benefits: [
      "Kit VIP diário",
      "Acesso a /fly por 1 hora/dia",
      "Tag [VIP] no chat",
      "5 homes extras",
      "Prioridade na fila",
    ],
    duration: "30 dias",
    durationDays: 30,
    featured: true,
  },
  {
    id: "vip-plus",
    categoryId: "vip",
    categorySlug: "vip",
    name: "VIP Plus",
    slug: "vip-plus",
    shortDescription: "VIP aprimorado com mais benefícios e recursos exclusivos.",
    description: "VIP aprimorado com mais benefícios e recursos exclusivos.",
    price: 39.90,
    originalPrice: 59.90,
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&q=80",
    benefits: [
      "Todos benefícios do VIP Básico",
      "Kit VIP Plus diário",
      "Acesso a /fly ilimitado",
      "Tag [VIP+] no chat",
      "10 homes extras",
      "Acesso ao /back",
    ],
    duration: "30 dias",
    durationDays: 30,
    featured: true,
  },
  {
    id: "vip-mvp",
    categoryId: "vip",
    categorySlug: "vip",
    name: "MVP",
    slug: "mvp",
    shortDescription: "O pacote definitivo para jogadores dedicados.",
    description: "O pacote definitivo para jogadores dedicados.",
    price: 79.90,
    originalPrice: 119.90,
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=400&q=80",
    benefits: [
      "Todos benefícios do VIP Plus",
      "Kit MVP exclusivo diário",
      "Acesso a todos comandos VIP",
      "Tag [MVP] animada no chat",
      "20 homes extras",
      "Acesso prioritário a eventos",
      "Suporte prioritário",
    ],
    duration: "30 dias",
    durationDays: 30,
    featured: true,
  },
  {
    id: "survival-coins",
    categoryId: "survival",
    categorySlug: "survival",
    name: "1000 Coins",
    slug: "1000-coins",
    shortDescription: "Pacote de 1000 coins para usar no Survival.",
    description: "Pacote de 1000 coins para usar no Survival.",
    price: 9.90,
    originalPrice: null,
    image: "https://images.unsplash.com/photo-1550100136-e092101726f4?w=400&q=80",
    benefits: [
      "1000 coins no Survival",
      "Entrega instantânea",
    ],
    duration: "Permanente",
    durationDays: null,
    featured: false,
  },
  {
    id: "skyblock-island",
    categoryId: "skyblock",
    categorySlug: "skyblock",
    name: "Ilha Premium",
    slug: "ilha-premium",
    shortDescription: "Comece com uma ilha premium no Skyblock.",
    description: "Comece com uma ilha premium no Skyblock.",
    price: 29.90,
    originalPrice: 49.90,
    image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=400&q=80",
    benefits: [
      "Ilha expandida 3x maior",
      "Kit inicial premium",
      "Spawner de porco incluso",
      "5 membros extras na ilha",
    ],
    duration: "Permanente",
    durationDays: null,
    featured: true,
  },
];

// Blog Posts Mock
export const blogPosts: BlogPost[] = [
  {
    id: "1",
    title: "Nova Temporada do Survival Chegando!",
    slug: "nova-temporada-survival",
    excerpt: "Prepare-se para a maior atualização do ano! Nova temporada do Survival com mapas inéditos, eventos especiais e muito mais.",
    content: `Estamos animados em anunciar a chegada da nova temporada do Survival! Esta será a maior atualização do ano, trazendo diversas novidades que vão transformar completamente a experiência de jogo.

A nova temporada incluirá mapas completamente inéditos, criados especialmente para proporcionar desafios únicos e recompensas exclusivas. Exploradores poderão descobrir biomas nunca antes vistos, com recursos raros e criaturas misteriosas.

Além disso, preparamos uma série de eventos especiais que acontecerão ao longo da temporada. Cada evento trará missões exclusivas, recompensas limitadas e a chance de conquistar títulos prestigiosos.

Os jogadores que participarem desde o primeiro dia receberão um kit inicial especial de boas-vindas! Não perca essa oportunidade de começar a temporada com vantagem.

A temporada inicia no próximo sábado às 18h. Prepare seu inventário, chame seus amigos e venha fazer parte desta nova era!`,
    image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=800&q=80",
    category: "Updates",
    author: {
      name: "Dryice",
      avatar: null,
      role: "Administrador",
    },
    date: "2025-01-18",
    views: 1250,
  },
  {
    id: "2",
    title: "Evento de Inverno - Recompensas Especiais",
    slug: "evento-inverno-recompensas",
    excerpt: "Participe do nosso evento de inverno e ganhe itens exclusivos, skins raras e muito mais!",
    content: `O inverno chegou ao servidor com um evento especial repleto de recompensas incríveis! Durante as próximas três semanas, todos os jogadores poderão participar de atividades temáticas e colecionar tokens especiais de inverno.

Complete missões diárias para acumular tokens que podem ser trocados por itens exclusivos na loja do evento. Entre as recompensas disponíveis estão skins raras com tema de inverno, pets especiais e até mesmo montarias únicas!

O evento conta com três fases distintas:
- Fase 1: Caça aos Cristais de Gelo - Colete cristais espalhados pelo mapa
- Fase 2: Boss de Inverno - Enfrente o poderoso Guardião das Neves
- Fase 3: Grande Batalha Final - Participe da guerra de clãs temática

Os 10 jogadores com mais pontos ao final do evento receberão um título exclusivo e permanente, além de um pacote especial de recompensas.

Não perca esta oportunidade única de colecionar itens que nunca mais estarão disponíveis!`,
    image: "https://images.unsplash.com/photo-1511512578047-dfb367046420?w=800&q=80",
    category: "Events",
    author: {
      name: "Dryice",
      avatar: null,
      role: "Administrador",
    },
    date: "2025-01-15",
    views: 890,
  },
  {
    id: "3",
    title: "Novo Sistema de Clãs Disponível",
    slug: "novo-sistema-clas",
    excerpt: "Crie seu clã, recrute membros e domine o servidor com o novo sistema de clãs!",
    content: `Finalmente está disponível o tão aguardado sistema de clãs! Agora você pode criar seu próprio clã, convidar amigos e participar de guerras épicas contra outros grupos.

O sistema de clãs foi desenvolvido para promover a cooperação entre jogadores e adicionar uma nova camada de estratégia ao jogo. Cada clã pode ter até 50 membros e possui seu próprio sistema de ranks internos.

Funcionalidades principais do sistema:
- Criação e customização de brasão do clã
- Sistema de níveis e experiência compartilhada
- Base do clã com território exclusivo
- Banco compartilhado entre membros
- Guerra de clãs com recompensas semanais
- Ranking global de clãs

Para criar um clã, você precisará ter pelo menos nível 20 e pagar uma taxa de 10.000 moedas. Clãs ativos e bem ranqueados receberão benefícios exclusivos, incluindo acesso a áreas especiais do mapa.

As guerras de clãs acontecerão todos os finais de semana, com confrontos estratégicos que testarão a coordenação e habilidade de cada grupo. Os clãs vencedores acumularão pontos no ranking e desbloquearão recompensas progressivas.

Reúna seus amigos e comece a construir o clã mais poderoso do servidor hoje mesmo!`,
    image: "https://images.unsplash.com/photo-1550100136-e092101726f4?w=800&q=80",
    category: "Updates",
    author: {
      name: "Dryice",
      avatar: null,
      role: "Administrador",
    },
    date: "2025-01-10",
    views: 2100,
  },
];

// User Mock
export const currentUser: User = {
  id: "1",
  username: "demo",
  email: "demo@example.com",
  avatar: null,
  role: "Admin",
  credits: 735.01,
  createdAt: "2024-06-15",
  isOnline: true,
};

// Tickets Mock
export const tickets: SupportTicket[] = [
  {
    id: "1",
    title: "Help me!",
    category: "Game",
    status: "open",
    lastUpdate: "2024-06-18T10:30:00Z",
    messages: [
      {
        id: "1",
        author: "demo",
        content: "Preciso de ajuda com minha conta.",
        date: "2024-06-18T10:30:00Z",
      },
    ],
  },
  {
    id: "2",
    title: "Problema com compra",
    category: "Billing",
    status: "closed",
    lastUpdate: "2024-06-10T14:20:00Z",
    messages: [],
  },
];

// Orders Mock
export const orders: any[] = [
  {
    id: "4",
    items: [{ productId: "vip-mvp", quantity: 1 }],
    total: 250.00,
    status: "completed",
    date: "2024-06-18",
    paymentMethod: "PIX",
  },
  {
    id: "1",
    items: [{ productId: "vip-basic", quantity: 1 }],
    total: 14.99,
    status: "completed",
    date: "2024-06-18",
    paymentMethod: "Credit Card",
  },
];

// Transactions Mock
export const transactions: Transaction[] = [
  {
    id: "1",
    type: "deposit",
    amount: 250.00,
    description: "Depósito via PIX",
    date: "2024-06-18",
  },
  {
    id: "2",
    type: "withdraw",
    amount: 14.99,
    description: "Compra - VIP Básico",
    date: "2024-06-18",
  },
];

// Wiki Articles Mock
export const wikiCategories: WikiCategory[] = [
  {
    id: "getting-started",
    name: "Primeiros Passos",
    icon: "Rocket",
    articles: [
      {
        id: "how-to-connect",
        title: "Como Conectar",
        content: "<h2>Como conectar ao servidor</h2><p>Para conectar ao servidor, siga os passos abaixo:</p><ol><li>Abra o Minecraft</li><li>Clique em Multiplayer</li><li>Adicione o servidor com o IP: <b>play.hyterra.net</b></li><li>Clique em Conectar</li></ol>",
      },
      {
        id: "first-steps",
        title: "Seus Primeiros Passos",
        content: "<h2>Bem-vindo ao Hyterra!</h2><p>Após conectar, você aparecerá no lobby principal. De lá você pode escolher o modo de jogo.</p>",
      },
    ],
  },
  {
    id: "gamemodes",
    name: "Modos de Jogo",
    icon: "Gamepad2",
    articles: [
      {
        id: "survival",
        title: "Survival",
        content: "<h2>Modo Survival</h2><p>O modo survival é o clássico minecraft com economia e proteção de terrenos.</p>",
      },
      {
        id: "skyblock",
        title: "Skyblock",
        content: "<h2>Modo Skyblock</h2><p>Comece em uma ilha flutuante e expanda seu território!</p>",
      },
    ],
  },
  {
    id: "commands",
    name: "Comandos",
    icon: "Terminal",
    articles: [
      {
        id: "basic-commands",
        title: "Comandos Básicos",
        content: "<h2>Comandos Básicos</h2><ul><li><b>/spawn</b> - Volta ao spawn</li><li><b>/home</b> - Vai para sua home</li><li><b>/tpa [player]</b> - Solicita teleporte</li></ul>",
      },
    ],
  },
];

// Ticket Categories
export const ticketCategories: any[] = [
  { id: "game", label: "Game" },
  { id: "billing", label: "Pagamento" },
  { id: "account", label: "Conta" },
  { id: "bug", label: "Bug Report" },
  { id: "other", label: "Outro" },
];

// Top Buyers - All Time
export const topBuyersAllTime: TopBuyer[] = [
  {
    id: "1",
    username: "Dryice",
    avatar: null,
    totalSpent: 2450.00,
    purchases: 47,
    rank: 1,
  },
  {
    id: "2",
    username: "NightWolf123",
    avatar: null,
    totalSpent: 1890.50,
    purchases: 32,
    rank: 2,
  },
  {
    id: "3",
    username: "DragonSlayer",
    avatar: null,
    totalSpent: 1654.00,
    purchases: 28,
    rank: 3,
  },
  {
    id: "4",
    username: "EliteGamer",
    avatar: null,
    totalSpent: 1230.00,
    purchases: 21,
    rank: 4,
  },
  {
    id: "5",
    username: "ProPlayer_BR",
    avatar: null,
    totalSpent: 980.00,
    purchases: 18,
    rank: 5,
  },
];

// Top Buyers - Monthly
export const topBuyersMonthly: TopBuyer[] = [
  {
    id: "1",
    username: "MysticKnight",
    avatar: null,
    totalSpent: 450.00,
    purchases: 8,
    rank: 1,
  },
  {
    id: "2",
    username: "ShadowHunter",
    avatar: null,
    totalSpent: 320.00,
    purchases: 5,
    rank: 2,
  },
  {
    id: "3",
    username: "ThunderBolt99",
    avatar: null,
    totalSpent: 275.50,
    purchases: 4,
    rank: 3,
  },
  {
    id: "4",
    username: "xKronos_",
    avatar: null,
    totalSpent: 190.00,
    purchases: 3,
    rank: 4,
  },
  {
    id: "5",
    username: "CyberNinja",
    avatar: null,
    totalSpent: 145.00,
    purchases: 2,
    rank: 5,
  },
];

// Store Goal/Meta
export const storeGoal: StoreGoal = {
  title: "Meta do Mês",
  description: "Ajude-nos a alcançar a meta para novos recursos!",
  currentAmount: 2750.00,
  targetAmount: 5000.00,
  rewards: [
    { threshold: 1000, description: "Evento Especial", unlocked: true },
    { threshold: 2500, description: "Novo Minigame", unlocked: true },
    { threshold: 5000, description: "Expansão do Mapa", unlocked: false },
  ],
  endsAt: "2025-02-28",
};

// Discord Widget Config
export const discordWidget: DiscordWidget = {
  serverId: "123456789",
  inviteLink: "https://discord.gg/hyterra",
  serverName: "Hyterra Community",
  membersOnline: 127,
  membersTotal: 5420,
};

export default storeConfig;
